// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ContentClient } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import type { Locale as AppLocale } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type {
  EiReport,
  EiReportingPeriod,
  EmploymentInsuranceApiClient,
  GuidedConversationView,
  GuidedEvent,
  GuidedSectionId,
  GuidedText,
} from 'employment-insurance-data-access';
import { usePageContents } from '../use-page-contents';
import { GUIDED_REPORTING_CONTENT_KEYS } from './content-keys';
import { createResolver } from './guided-text';
import { ProgressRail } from './ProgressRail';
import { QuestionInput } from './QuestionInput';
import { ReportSubmitted } from './ReportSubmitted';
import { ReviewPage } from './ReviewPage';
import { Transcript } from './Transcript';
import './GuidedReport.css';

export interface GuidedReportProps {
  apiClient: EmploymentInsuranceApiClient;
  contentClient: ContentClient;
  locale: AppLocale;
  period: EiReportingPeriod;
  nextPeriod: EiReportingPeriod | undefined;
  /** Back to the report overview; `submitted` when a report was just filed. */
  onExit: (submitted: boolean) => void;
}

const SECTION_BY_NUMBER: Record<number, GuidedSectionId> = {
  1: 'absences',
  2: 'work',
  3: 'fullTimeJob',
  4: 'training',
  5: 'availability',
  6: 'otherMoney',
};

/**
 * EI guided reporting (docs/specs/018-ei-guided-reporting.md), opened by
 * Review on the report overview: the render layer only. The BFF owns the
 * conversation -- every answer, check and flag -- and this component sends
 * one event per turn and renders the state that comes back. A reload
 * re-reads the same state (GET /reporting/conversation); view state such as
 * an open form's half-typed values is not kept.
 *
 * Layout from the Figma prototype's GuidedReport.tsx: the conversation with
 * its section header and Back, and a "Your report" rail beside it (above it
 * on a narrow screen); then the review page, then the confirmation.
 *
 * Accessibility: the transcript is a `role="log"` region, so each new turn
 * is announced once as it is appended. After a turn, focus goes to the
 * answer area (labelled "Your answer", not the question text, so nothing is
 * read twice); the review and confirmation pages focus their own <h1>.
 */
export function GuidedReport({ apiClient, contentClient, locale, period, nextPeriod, onExit }: GuidedReportProps) {
  const { content, loadedLocale } = usePageContents(contentClient, GUIDED_REPORTING_CONTENT_KEYS, locale);
  const dateLocale = locale === 'fr' ? 'fr' : 'en';
  const t = useMemo(() => createResolver(content, dateLocale), [content, dateLocale]);

  const [view, setView] = useState<GuidedConversationView | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [turnError, setTurnError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitted, setSubmitted] = useState<EiReport | null>(null);
  const answerRef = useRef<HTMLDivElement>(null);
  const focusAnswer = useRef(false);
  // One request in flight at a time. A ref, not state: `busy` and
  // `submitting` only disable the controls after the next render, so a
  // double click inside one tick would otherwise send twice -- and turns and
  // submit are read-modify-write on a cache with no atomic operations, so a
  // second request could lose a turn or file a second report for the period.
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getGuidedConversation(period.periodStart)
      .then((loaded) => {
        if (!cancelled) setView(loaded);
      })
      .catch((err) => {
        console.error('Failed to load the EI guided report', err);
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, period.periodStart]);

  useEffect(() => {
    if (focusAnswer.current && view?.phase === 'conversation') {
      focusAnswer.current = false;
      answerRef.current?.focus();
    }
  }, [view]);

  // After the server refuses a turn or a submit, re-read the conversation: the
  // saved state may have been replaced under this tab (a deploy that changed
  // the saved shape starts the citizen afresh), so this tab's view is stale.
  // If the re-read fails too, the current view stays as it is.
  const resync = useCallback(async (): Promise<GuidedConversationView | null> => {
    try {
      const fresh = await apiClient.getGuidedConversation(period.periodStart);
      setView(fresh);
      return fresh;
    } catch (err) {
      console.error('Failed to re-read the EI guided report', err);
      return null;
    }
  }, [apiClient, period.periodStart]);

  const send = useCallback(
    async (event: GuidedEvent): Promise<void> => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setBusy(true);
      setTurnError(false);
      try {
        const next = await apiClient.sendGuidedTurn(period.periodStart, event);
        focusAnswer.current = true;
        setView(next);
      } catch (err) {
        console.error('Failed to send an EI guided-report turn', err);
        setTurnError(true);
        await resync();
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    },
    [apiClient, period.periodStart, resync],
  );

  async function submit(): Promise<void> {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setSubmitError(false);
    try {
      setSubmitted(await apiClient.submitGuidedReport(period.periodStart));
    } catch (err) {
      console.error('Failed to submit the EI guided report', err);
      setSubmitError(true);
      // A conversation that is no longer at review was replaced: the submit that
      // failed belonged to the old one, so drop its error (or it would resurface
      // on the fresh conversation's review page) and say so on the page that's left.
      const fresh = await resync();
      if (fresh && fresh.phase !== 'review') {
        setSubmitError(false);
        setTurnError(true);
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  // Nothing renders until this locale's copy has arrived: no raw keys.
  if (loadedLocale === undefined) {
    return null;
  }

  if (loadError) {
    return (
      <p role="alert" className="ei-guided__note ei-guided__note--error">
        {t({ key: 'loadError' })}
      </p>
    );
  }
  if (!view) {
    return null;
  }

  if (submitted) {
    return <ReportSubmitted report={submitted} period={view.period} nextPeriod={nextPeriod} t={t} onDone={() => onExit(true)} />;
  }

  const periodRange = { t: 'range' as const, from: view.period.firstDay, to: view.period.lastDay };
  const turnErrorNote = turnError ? (
    <p role="alert" className="ei-guided__note ei-guided__note--error">
      {t({ key: 'turnError' })}
    </p>
  ) : null;

  if (view.phase === 'review' && view.review) {
    return (
      <div className="ei-guided">
        {turnErrorNote}
        <ReviewPage
          view={view}
          review={view.review}
          t={t}
          busy={busy}
          submitting={submitting}
          submitError={submitError}
          onChange={(section) => void send({ type: 'change', section })}
          onAttest={(confirmed) => void send({ type: 'attest', confirmed })}
          onBack={() => void send({ type: 'back' })}
          onSubmit={() => void submit()}
        />
      </div>
    );
  }

  const current = view.current;
  const sectionId = current ? SECTION_BY_NUMBER[current.section] : undefined;
  const sectionName = sectionId ? t({ key: `section.${sectionId}` }) : '';
  const navText = view.editing
    ? t({ key: 'navEditing', params: { section: { t: 'text', v: view.editing === 'intro' ? t({ key: 'review.introHeading' }) : t({ key: `section.${view.editing}` }) } } })
    : sectionId
      ? t({ key: 'navStep', params: { n: { t: 'number', v: current?.section ?? 0 }, section: { t: 'text', v: sectionName } } })
      : t({ key: 'navStart' });
  const done = view.progress.filter((section) => section.status === 'done').length;
  const lastQuestion = [...view.transcript].reverse().find((event) => event.kind === 'question');
  const question: GuidedText | null = lastQuestion && lastQuestion.kind === 'question' ? lastQuestion.text : null;
  // Back to the previous question; at the very start, back out to the overview.
  const canExit = !view.canGoBack && !view.editing;

  return (
    <div className="ei-guided">
      <div className="ei-guided__header">
        <h1 className="ei-guided__title">{t({ key: 'title' })}</h1>
        <p className="ei-guided__subtitle">{t({ key: 'subtitle', params: { period: periodRange } })}</p>
      </div>
      <div className="ei-guided__layout">
        <section className="ei-guided__conversation" aria-label={t({ key: 'title' })}>
          <div className="ei-guided__nav">
            <span className="ei-guided__nav-section">{navText}</span>
            {view.canGoBack || canExit ? (
              <button
                type="button"
                className="ei-guided__back"
                data-testid="guided-back"
                disabled={busy}
                onClick={() => (view.canGoBack ? void send({ type: 'back' }) : onExit(false))}
              >
                <span aria-hidden="true">‹ </span>
                {t({ key: 'back' })}
              </button>
            ) : null}
            <div className="ei-guided__nav-bar" aria-hidden="true">
              <span style={{ width: `${Math.round((done / 6) * 100)}%` }} />
            </div>
          </div>
          <Transcript events={view.transcript} t={t} label={t({ key: 'transcriptLabel' })} />
          {current ? (
            <div className="ei-guided__answer" ref={answerRef} tabIndex={-1} role="group" aria-label={t({ key: 'answerLabel' })} data-testid="guided-answer">
              {turnErrorNote}
              <QuestionInput
                // A new question remounts the form with the BFF's values; a
                // validation error (same question) keeps what was typed.
                key={`${current.stepId}-${lastQuestion?.id ?? 0}`}
                input={current.input}
                question={question}
                period={view.period}
                t={t}
                locale={dateLocale}
                busy={busy}
                contentKey={loadedLocale}
                onAnswer={(value) => current.stepId !== 'flag' && void send({ type: 'answer', stepId: current.stepId, value })}
                onFlagResponse={(choice) => void send({ type: 'flagResponse', choice })}
              />
            </div>
          ) : null}
        </section>
        <ProgressRail progress={view.progress} t={t} />
      </div>
    </div>
  );
}
